import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChatBubbleLeftIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import {
  IInboxConversation,
  InboxConversationChannel,
  InboxConversationStatus,
  UserTypes,
} from 'shared';

import { Badge } from '../../../common/Atoms/Badge';
import { Button } from '../../../common/Atoms/Button';
import { Card } from '../../../common/Atoms/Card';
import { MultiSelect } from '../../../common/Atoms/MultiSelect';
import { Select } from '../../../common/Atoms/Select';
import { Paragraph } from '../../../common/Atoms/Typography/Paragraph';
import { classNames } from '../../../common/lib/classNames';
import { useCurrentOrgUsers } from '../../hooks/useCurrentOrgUsers';
import { useGetSelectedOrganisation } from '../../hooks/useGetSelectedOrganisation';
import { useGetInboxConversationsQuery } from '../../services/api/inboxConversationApi/inboxConversation';
import { ContactAvatar } from '../Agents/Conversations/ContactAvatar';
import { formatLocalTimestamp } from '../Agents/Conversations/conversationHelpers';
import { LoadingOverlay } from '../Molecules/LoadingOverlay';

import { ConversationDetail } from './ConversationDetail';

type TypeFilter = `all` | InboxConversationChannel | `unmatched`;

const SEARCH_DEBOUNCE_MS = 300;
const STATUS_OPTIONS = [
  { value: InboxConversationStatus.OPEN, label: `Open` },
  { value: InboxConversationStatus.RESOLVED, label: `Resolved` },
  { value: InboxConversationStatus.ARCHIVED, label: `Archived` },
];
const TYPE_OPTIONS: Array<{ value: TypeFilter; label: string }> = [
  { value: `all`, label: `All types` },
  { value: InboxConversationChannel.EMAIL, label: `Email` },
  { value: InboxConversationChannel.SMS, label: `SMS` },
  { value: `unmatched`, label: `No contact` },
];
const EVERYONE = `everyone`;

function ConversationRow({ conversation, selected, onSelect }: {
  conversation: IInboxConversation;
  selected: boolean;
  onSelect: (conversationId: string) => void;
}) {
  const ChannelIcon = conversation.channel === InboxConversationChannel.SMS ? ChatBubbleLeftIcon : EnvelopeIcon;

  return (
    <button
      type={ `button` }
      className={ classNames(
        `w-full cursor-pointer rounded-lg border px-3.5 py-3 text-left transition-colors`,
        selected ?
          `border-paidniceGreen-300 bg-paidniceGreen-50 shadow-xs` :
          `border-transparent hover:border-gray-200 hover:bg-gray-50`,
      ) }
      onClick={ () => onSelect(conversation.id) }
    >
      <div className={ `flex items-start gap-3` }>
        <ContactAvatar
          name={ conversation.contactName || conversation.sender }
          alert={ !conversation.contactId }
        />
        <div className={ `min-w-0 flex-1` }>
          <div className={ `flex items-center justify-between gap-2` }>
            <Paragraph
              size={ `sm` }
              className={ classNames(`truncate`, conversation.unread && `font-semibold`) }
            >
              { conversation.contactName || conversation.sender || `Unknown sender` }
            </Paragraph>
            <span className={ `flex shrink-0 items-center gap-2` }>
              {
                conversation.status !== InboxConversationStatus.OPEN &&
                <Badge color={ conversation.status === InboxConversationStatus.RESOLVED ? `green` : `gray` }>
                  { STATUS_OPTIONS.find(option => option.value === conversation.status)?.label }
                </Badge>
              }
              {
                conversation.unread &&
                <span
                  aria-label={ `Unread` }
                  className={ `h-2 w-2 rounded-full bg-paidniceGreen-500` }
                />
              }
            </span>
          </div>
          <Paragraph
            size={ `xs` }
            variant={ `secondary` }
            className={ `mt-1 line-clamp-2` }
          >
            { conversation.title || `No subject` }
          </Paragraph>
          <div className={ `mt-2 flex items-center justify-between gap-2 text-xs text-gray-400` }>
            <span className={ `flex items-center gap-1` }>
              <ChannelIcon className={ `h-3.5 w-3.5` } />
              { formatLocalTimestamp(String(conversation.lastInboundAt)) }
            </span>
            <span className={ `truncate` }>
              { conversation.assignee ? (conversation.assignee.firstName || conversation.assignee.email) : `` }
            </span>
          </div>
        </div>
      </div>
    </button>
  );
}

export function ConversationsPage() {
  const navigate = useNavigate();
  const { conversationId } = useParams<{ conversationId?: string }>();
  const organisationId = useGetSelectedOrganisation()?.id;
  const users = useCurrentOrgUsers(true, [UserTypes.Inherit, UserTypes.Invited]);
  const [statuses, setStatuses] = useState([InboxConversationStatus.OPEN]);
  const [type, setType] = useState<TypeFilter>(`all`);
  const [assignee, setAssignee] = useState(EVERYONE);
  const [searchInput, setSearchInput] = useState(``);
  const [search, setSearch] = useState(``);
  // Cursors of the pages gone through so far. The last one is the page showing, none is the first page
  const [cursors, setCursors] = useState<string[]>([]);

  useEffect(() => {
    const timeout = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    setCursors([]);
  }, [statuses, type, assignee, search]);

  const { data, isLoading, error } = useGetInboxConversationsQuery({
    organisationId: organisationId || ``,
    statuses,
    channel: type === `all` || type === `unmatched` ? undefined : type,
    unmatched: type === `unmatched` || undefined,
    assignee: assignee === EVERYONE ? undefined : assignee,
    search: search || undefined,
    before: cursors[cursors.length - 1],
  }, {
    skip: !organisationId,
    pollingInterval: 15000,
    skipPollingIfUnfocused: true,
  });

  if (!organisationId) {
    return null;
  }

  const conversations = data?.conversations || [];

  return (
    <Card>
      <div className={ `grid grid-cols-1 gap-4 lg:grid-cols-3` }>
        <div className={ `overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xs lg:col-span-1 lg:max-h-[calc(100vh-160px)]` }>
          <div className={ `space-y-2 border-b border-gray-200 px-4 py-3` }>
            <input
              type={ `search` }
              aria-label={ `Search conversations` }
              placeholder={ `Search by contact, sender or subject` }
              className={ `w-full rounded-md border border-gray-200 px-2.5 py-1.5 text-xs text-gray-700 focus:border-paidniceGreen-300 focus:ring-0 focus:outline-hidden` }
              value={ searchInput }
              onChange={ event => setSearchInput(event.target.value) }
            />
            <div className={ `grid grid-cols-3 gap-2` }>
              <MultiSelect
                ariaLabel={ `Status` }
                options={ STATUS_OPTIONS }
                selected={ statuses }
                display={ STATUS_OPTIONS.filter(option => statuses.includes(option.value)).map(option => option.label).join(`, `) }
                // The list always shows at least one status
                onChange={ value => value.length && setStatuses(value as InboxConversationStatus[]) }
                size={ `sm` }
                containerClassname={ `min-h-0 min-w-0` }
              />
              <Select
                options={ TYPE_OPTIONS }
                selected={ type }
                onChange={ ({ value }) => setType(value as TypeFilter) }
                size={ `sm` }
                className={ `min-w-0` }
              />
              <Select
                options={ [
                  { value: EVERYONE, label: `Everyone` },
                  { value: `me`, label: `Mine` },
                  { value: `unassigned`, label: `Unassigned` },
                  ...users,
                ] }
                selected={ assignee }
                onChange={ ({ value }) => setAssignee(String(value)) }
                size={ `sm` }
                className={ `min-w-0` }
              />
            </div>
          </div>

          <LoadingOverlay
            loading={ isLoading }
            className={ `min-h-[160px]` }
          >
            <div className={ `space-y-1 p-2 lg:max-h-[calc(100vh-330px)] lg:overflow-y-auto` }>
              {
                !!error &&
                <Paragraph
                  variant={ `error` }
                  className={ `p-4` }
                >
                  { `Couldn't load conversations.` }
                </Paragraph>
              }
              {
                !isLoading && !error && !conversations.length &&
                <Paragraph
                  variant={ `secondary` }
                  className={ `p-4` }
                >
                  { `No conversations here.` }
                </Paragraph>
              }
              {
                conversations.map(conversation => (
                  <ConversationRow
                    key={ conversation.id }
                    conversation={ conversation }
                    selected={ conversation.id === conversationId }
                    onSelect={ id => navigate(`/${organisationId}/conversations/${id}`) }
                  />
                ))
              }
            </div>
          </LoadingOverlay>

          {
            (cursors.length > 0 || !!data?.nextCursor) &&
            <div className={ `flex items-center justify-between border-t border-gray-100 px-4 py-3` }>
              <Button
                color={ `gray` }
                size={ `sm` }
                disabled={ !cursors.length }
                onClick={ () => setCursors(previous => previous.slice(0, -1)) }
              >
                { `Previous` }
              </Button>
              <Button
                color={ `gray` }
                size={ `sm` }
                disabled={ !data?.nextCursor }
                onClick={ () => data?.nextCursor && setCursors(previous => [...previous, data.nextCursor as string]) }
              >
                { `Next` }
              </Button>
            </div>
          }
        </div>

        <div className={ `lg:col-span-2 lg:max-h-[calc(100vh-160px)] lg:overflow-y-auto lg:pr-1` }>
          {
            conversationId ?
              <ConversationDetail
                // Composer drafts and the picked contact belong to the conversation they were started on
                key={ conversationId }
                organisationId={ organisationId }
                conversationId={ conversationId }
              /> :
              <div className={ `flex h-full items-center justify-center rounded-lg border border-dashed border-gray-200 bg-white p-10 shadow-xs` }>
                <Paragraph variant={ `secondary` }>
                  { `Select a conversation to see the details.` }
                </Paragraph>
              </div>
          }
        </div>
      </div>
    </Card>
  );
}
